import { itemizeCartLine, PER_UNIT_SUFFIX } from '@order-up/common';
import { type CartItem } from '@order-up/models';
import { ChevronDown, ChevronRight, ShoppingCart, X } from 'lucide-react';
import { useState } from 'react';
import LazyOnlineServiceFeeRow from './LazyOnlineServiceFeeRow';

/**
 * The price beside a cart line's name: its BASE when the line has priced
 * add-ons (they are listed beneath with their own "+$", and the line closes on
 * an "Item total"), its total otherwise — `itemizeCartLine`, the rule the till,
 * the receipt and the back office all follow. Showing the total up top above
 * "+$" add-ons read as the add-ons charged twice (House of Pho, 2026-09-23).
 */
function linePriceLabel(item: CartItem): string {
    const itemized = itemizeCartLine(item);
    return itemized
        ? `$${(itemized.unitMinor / 100).toFixed(2)}${itemized.perUnit ? PER_UNIT_SUFFIX : ''}`
        : `$${item.totalPrice.toFixed(2)}`;
}

/** The closing "Item total" row of an itemized line, or nothing. */
function LineItemTotal({ item }: { item: CartItem }) {
    const itemized = itemizeCartLine(item);
    if (!itemized) return null;
    return (
        <div
            className="flex items-center justify-between border-t border-gray-100 pt-0.5 font-semibold text-gray-900"
            data-testid="item-total"
        >
            <span>{itemized.totalLabel}</span>
            <span>${item.totalPrice.toFixed(2)}</span>
        </div>
    );
}

interface OrderSummaryProps {
    subtotal: number;
    taxAndFees: number;
    tipAmount: number;
    deliveryFee: number;
    total: number;
    /**
     * Server-derived discount already SUBTRACTED from `total` (dollars), e.g. a
     * loyalty/rewards redemption. When > 0 we render an explicit "Rewards
     * discount −$X" line so the visible rows reconcile exactly to `total`
     * (without it, subtotal + taxes + tip + delivery over-sums the Total with no
     * explanation).
     */
    discountAmount?: number;
    /**
     * Server-derived coupon / promo-code discount already SUBTRACTED from `total`
     * (dollars), SEPARATE from `discountAmount` (loyalty). When > 0 we render an
     * explicit "Coupon (CODE) −$X" line so the visible rows still reconcile to
     * `total` (subtotal + taxes + tip + delivery − loyalty − coupon). Both
     * discounts can co-exist.
     */
    couponAmount?: number;
    /** The applied promo code, shown in the coupon line label (e.g. "SAVE10"). */
    couponCode?: string;
    cartItems: CartItem[];
    /**
     * True while the server order preview is still in flight. Subtotal, tip and
     * the line items are known from the cart and render immediately; the
     * server-derived amounts (taxes & fees, grand total) show an inline skeleton
     * instead of a placeholder "$0.00" until the preview resolves.
     */
    pending?: boolean;
    /** The Online Service Fee (already inside `total`; its tax is inside `taxAndFees`). Null/absent → no row. */
    onlineServiceFee?: { amount: number; tax: number; label: string } | null;
    /** The restaurant's name, for the fee's explanation. */
    locationName?: string;
}

/** Inline money value that becomes a skeleton bar while its amount is pending. */
function Money({ value, pending, className = '', barWidth = 'w-16' }: { value: number; pending?: boolean; className?: string; barWidth?: string }) {
    if (pending) {
        return (
            <span
                className={`inline-block h-5 ${barWidth} rounded bg-gray-200 motion-safe:animate-pulse align-middle`}
                aria-hidden="true"
            />
        );
    }
    return <span className={className}>${value.toFixed(2)}</span>;
}

export default function OrderSummary({ subtotal, taxAndFees, tipAmount, deliveryFee, total, discountAmount = 0, couponAmount = 0, couponCode, cartItems, pending, onlineServiceFee, locationName }: OrderSummaryProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Calculate total item count
    const totalItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <>
            {/* Desktop card - Hidden on mobile/tablet. The page's sidebar column
                owns the width + stickiness so the summary, loyalty and promo
                controls travel as one sticky stack. */}
            <div className="hidden lg:block">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">Order summary</h2>

                    {/* Summary Items */}
                    <div className="space-y-4 mb-6">
                        <div className="flex items-center justify-between text-base">
                            <span className="text-gray-700">Subtotal</span>
                            <span className="font-semibold text-gray-900">${subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between text-base">
                            <button className="flex items-center gap-1 text-gray-700 hover:text-gray-900">
                                <span>Taxes & fees</span>
                                <ChevronDown className="h-4 w-4" />
                            </button>
                            <Money value={taxAndFees} pending={pending} className="font-semibold text-gray-900" />
                        </div>
                        {onlineServiceFee && onlineServiceFee.amount > 0 && (
                            <LazyOnlineServiceFeeRow fee={onlineServiceFee} locationName={locationName} />
                        )}
                        <div className="flex items-center justify-between text-base">
                            <span className="text-gray-700">Tip</span>
                            <span className="font-semibold text-gray-900">${tipAmount.toFixed(2)}</span>
                        </div>
                        {deliveryFee > 0 && (
                            <div className="flex items-center justify-between text-base">
                                <span className="text-gray-700">Delivery fee</span>
                                <span className="font-semibold text-gray-900">${deliveryFee.toFixed(2)}</span>
                            </div>
                        )}
                        {discountAmount > 0 && (
                            <div className="flex items-center justify-between text-base">
                                <span className="text-gray-700">Rewards discount</span>
                                <span className="font-semibold text-green-700">−${discountAmount.toFixed(2)}</span>
                            </div>
                        )}
                        {couponAmount > 0 && (
                            <div className="flex items-center justify-between text-base">
                                <span className="text-gray-700">{couponCode ? `Coupon (${couponCode})` : 'Coupon'}</span>
                                <span className="font-semibold text-green-700">−${couponAmount.toFixed(2)}</span>
                            </div>
                        )}
                    </div>

                    {/* Total */}
                    <div className="border-t border-gray-200 pt-4 mb-6">
                        <div className="flex items-center justify-between text-lg">
                            <span className="font-bold text-gray-900">Total</span>
                            <Money value={total} pending={pending} className="font-bold text-gray-900" barWidth="w-20" />
                        </div>
                    </div>

                    {/* Cart Items */}
                    <div className="space-y-4">
                        {cartItems.map((item) => (
                            <div key={item.cartItemId} className="flex items-start gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <h3 className="font-semibold text-gray-900 text-sm">
                                            {item.quantity > 1 && `${item.quantity}x `}
                                            {item.name}
                                        </h3>
                                        <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                                            {linePriceLabel(item)}
                                        </span>
                                    </div>

                                    {/* Size & Modifiers */}
                                    {(item.selectedSize || item.modifierGroups.some(group =>
                                        group.modifiers.some(mod => mod.priceDelta > 0)
                                    )) && (
                                        <div className="text-xs space-y-0.5 mt-1">
                                            {/* Size (show selected size name) */}
                                            {item.selectedSize && (
                                                <div className="flex items-center justify-between">
                                                    <span className="text-gray-600">
                                                        {item.selectedSize.sizeName}
                                                    </span>
                                                </div>
                                            )}
                                            {/* Modifier upcharges (paid modifiers only) */}
                                            {item.modifierGroups.map((group, groupIdx) =>
                                                group.modifiers.map((mod, modIdx) => (
                                                    mod.priceDelta > 0 && (
                                                        <div key={`${groupIdx}-${modIdx}`} className="flex items-center justify-between">
                                                            <span className="text-gray-600">+ {mod.name}{mod.quantity > 1 ? ` (×${mod.quantity})` : ''}</span>
                                                            <span className="text-gray-600">+${mod.totalPriceDelta.toFixed(2)}</span>
                                                        </div>
                                                    )
                                                ))
                                            )}
                                            <LineItemTotal item={item} />
                                        </div>
                                    )}

                                    {/* Notes */}
                                    {item.notes && (
                                        <p className="text-xs text-gray-600 mt-1.5 italic">Note: {item.notes}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Mobile/Tablet - Clickable Card */}
            <button
                onClick={() => setIsModalOpen(true)}
                className="lg:hidden flex items-center justify-between w-full p-4 bg-white rounded-2xl border border-gray-200 hover:border-gray-300 transition-all"
            >
                <div className="flex items-center gap-3">
                    <ShoppingCart className="h-5 w-5 text-gray-700" />
                    <span className="font-semibold text-gray-900">
                        {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <Money value={total} pending={pending} className="font-bold text-gray-900" barWidth="w-16" />
                    <ChevronRight className="h-5 w-5 text-gray-600" />
                </div>
            </button>

            {/* Modal/Bottom Sheet */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 md:flex md:items-center md:justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setIsModalOpen(false)}
                    />

                    {/* Modal Content - Bottom sheet on mobile, centered modal on tablet */}
                    <div className="absolute inset-x-0 bottom-0 md:relative md:inset-auto bg-white md:rounded-2xl rounded-t-2xl max-h-[85vh] md:max-h-[80vh] overflow-hidden flex flex-col md:w-[480px] md:animate-none animate-slide-up">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900">Order summary</h2>
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                aria-label="Close"
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors pointer-coarse:p-3"
                            >
                                <X className="h-5 w-5 text-gray-600" aria-hidden />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Summary Items */}
                            <div className="space-y-4 mb-6">
                                <div className="flex items-center justify-between text-base">
                                    <span className="text-gray-700">Subtotal</span>
                                    <span className="font-semibold text-gray-900">${subtotal.toFixed(2)}</span>
                                </div>
                                <div className="flex items-center justify-between text-base">
                                    <button className="flex items-center gap-1 text-gray-700 hover:text-gray-900">
                                        <span>Taxes & fees</span>
                                        <ChevronDown className="h-4 w-4" />
                                    </button>
                                    <Money value={taxAndFees} pending={pending} className="font-semibold text-gray-900" />
                                </div>
                                {onlineServiceFee && onlineServiceFee.amount > 0 && (
                                    <LazyOnlineServiceFeeRow fee={onlineServiceFee} locationName={locationName} />
                                )}
                                <div className="flex items-center justify-between text-base">
                                    <span className="text-gray-700">Tip</span>
                                    <span className="font-semibold text-gray-900">${tipAmount.toFixed(2)}</span>
                                </div>
                                {deliveryFee > 0 && (
                                    <div className="flex items-center justify-between text-base">
                                        <span className="text-gray-700">Delivery fee</span>
                                        <span className="font-semibold text-gray-900">${deliveryFee.toFixed(2)}</span>
                                    </div>
                                )}
                                {discountAmount > 0 && (
                                    <div className="flex items-center justify-between text-base">
                                        <span className="text-gray-700">Rewards discount</span>
                                        <span className="font-semibold text-green-700">−${discountAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {couponAmount > 0 && (
                                    <div className="flex items-center justify-between text-base">
                                        <span className="text-gray-700">{couponCode ? `Coupon (${couponCode})` : 'Coupon'}</span>
                                        <span className="font-semibold text-green-700">−${couponAmount.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>

                            {/* Total */}
                            <div className="border-t border-gray-200 pt-4 mb-6">
                                <div className="flex items-center justify-between text-lg">
                                    <span className="font-bold text-gray-900">Total</span>
                                    <Money value={total} pending={pending} className="font-bold text-gray-900" barWidth="w-20" />
                                </div>
                            </div>

                            {/* Cart Items */}
                            <div className="space-y-4">
                                {cartItems.map((item) => (
                                    <div key={item.cartItemId} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2 mb-1">
                                                <h3 className="font-semibold text-gray-900">
                                                    {item.quantity > 1 && `${item.quantity}x `}
                                                    {item.name}
                                                </h3>
                                                <span className="font-bold text-gray-900 whitespace-nowrap">
                                                    {linePriceLabel(item)}
                                                </span>
                                            </div>

                                            {/* Size & Modifiers */}
                                            {(item.selectedSize || item.modifierGroups.some(group =>
                                                group.modifiers.some(mod => mod.priceDelta > 0)
                                            )) && (
                                                <div className="text-xs space-y-0.5 mb-1">
                                                    {/* Size (show selected size name) */}
                                                    {item.selectedSize && (
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-gray-600">
                                                                {item.selectedSize.sizeName}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {/* Modifier upcharges (paid modifiers only) */}
                                                    {item.modifierGroups.map((group, groupIdx) =>
                                                        group.modifiers.map((mod, modIdx) => (
                                                            mod.priceDelta > 0 && (
                                                                <div key={`${groupIdx}-${modIdx}`} className="flex items-center justify-between">
                                                                    <span className="text-gray-600">+ {mod.name}{mod.quantity > 1 ? ` (×${mod.quantity})` : ''}</span>
                                                                    <span className="text-gray-600">+${mod.totalPriceDelta.toFixed(2)}</span>
                                                                </div>
                                                            )
                                                        ))
                                                    )}
                                                    <LineItemTotal item={item} />
                                                </div>
                                            )}

                                            {/* Notes */}
                                            {item.notes && (
                                                <p className="text-xs text-gray-600 italic">Note: {item.notes}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Footer Button */}
                        <div className="p-6 border-t border-gray-200">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="w-full bg-red-700 hover:bg-red-800 text-white font-semibold py-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                                <span>Done</span>
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

